import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { v4 as uuid } from 'uuid'

import { safeLocalStorage, safeSessionStorage } from '@hooks/useStorage'

import { NoticeProps } from '@shared/components/Notice'

export type PageNoticeType = 'neutral' | 'danger' | 'success' | 'warning' | 'info' | 'dark'

enum PageNoticeActionKind {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

interface PageNoticeAction {
  type: PageNoticeActionKind
  notice?: NoticeProps
  removeId?: string
}

type AddPageNoticeAction = Omit<NoticeProps, 'id' | 'type'> & {
  id?: string
  type?: PageNoticeType
}

interface PageNoticeContextValue {
  notices: NoticeProps[]
  add: (notice: AddPageNoticeAction) => string
  remove: (id: string) => void
}

const PageNoticeContext = createContext<PageNoticeContextValue | undefined>(undefined)

const NOTICE_STORAGE_KEY_PREFIX = 'pageNotice_'
const SESSION_STORAGE_KEY_PREFIX = 'pageNoticeSession_'

const getNoticeState = (
  id: string
): { dismissed: boolean; timestamp: number; dismissDuration?: number } | null => {
  const storedState = safeLocalStorage.getItem(`${NOTICE_STORAGE_KEY_PREFIX}${id}`)
  return storedState ? JSON.parse(storedState) : null
}

const setNoticeState = (
  id: string,
  dismissed: boolean,
  dismissType: NoticeProps['dismissType'],
  dismissDuration?: number
): void => {
  if (dismissType === 'session') {
    safeSessionStorage.setItem(
      `${SESSION_STORAGE_KEY_PREFIX}${id}`,
      JSON.stringify({ dismissed, timestamp: Date.now() })
    )
  } else if (dismissType === 'permanent' || dismissType === 'temporary') {
    safeLocalStorage.setItem(
      `${NOTICE_STORAGE_KEY_PREFIX}${id}`,
      JSON.stringify({ dismissed, timestamp: Date.now(), dismissDuration })
    )
  }
}

const shouldShowNotice = (
  id: string,
  dismissType?: NoticeProps['dismissType'],
  dismissDuration?: number
): boolean => {
  if (dismissType === 'session') {
    const sessionState = safeSessionStorage.getItem(`${SESSION_STORAGE_KEY_PREFIX}${id}`)
    return !sessionState || !JSON.parse(sessionState).dismissed
  }

  const state = getNoticeState(id)

  if (!state) {
    return true
  }

  if (state.dismissed) {
    if (dismissType === 'permanent') {
      return false
    }

    if (dismissType === 'temporary' && dismissDuration) {
      const durationInMs = dismissDuration * 24 * 60 * 60 * 1000
      const timeSinceDismissed = Date.now() - state.timestamp
      return timeSinceDismissed >= durationInMs
    }
  }

  return !state.dismissed
}

const reducer = (state: NoticeProps[], action: PageNoticeAction): NoticeProps[] => {
  switch (action.type) {
    case PageNoticeActionKind.ADD: {
      if (!action.notice) {
        return state
      }
      const existingIndex = state.findIndex((notice) => notice.id === action.notice?.id)
      if (existingIndex >= 0) {
        const newState = [...state]
        newState[existingIndex] = action.notice
        return newState
      }
      return [...state, action.notice]
    }
    case PageNoticeActionKind.REMOVE:
      return state.filter((notice) => notice.id !== action.removeId)
    default:
      return state
  }
}

const isSameNotice = (a: NoticeProps, b: NoticeProps): boolean => {
  if (a.id !== b.id) {
    return false
  }
  return (
    a.type === b.type &&
    a.title === b.title &&
    a.message === b.message &&
    a.canClose === b.canClose &&
    a.dismissType === b.dismissType &&
    a.dismissDuration === b.dismissDuration
  )
}

const isNoticeListEqual = (arr1: NoticeProps[], arr2: NoticeProps[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false
  }
  const map = new Map<string, NoticeProps>(arr1.map((n) => [n.id, n]))
  for (const n of arr2) {
    const existing = map.get(n.id)
    if (!existing || !isSameNotice(existing, n)) {
      return false
    }
  }
  return true
}

export const PageNoticeContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [notices, dispatch] = useReducer(reducer, [])
  const location = useLocation()
  const [visibleNotices, setVisibleNotices] = useState<NoticeProps[]>([])
  const prevFilteredRef = useRef<NoticeProps[]>([])

  const add = useCallback((notice: AddPageNoticeAction): string => {
    const id = notice.id || uuid()
    const newNotice = { ...notice, id, type: notice.type || 'neutral' } as NoticeProps
    dispatch({ type: PageNoticeActionKind.ADD, notice: newNotice })
    return id
  }, [])

  const remove = useCallback((id: string): void => {
    dispatch({
      type: PageNoticeActionKind.REMOVE,
      removeId: id,
    })
  }, [])

  useEffect(() => {
    const filtered = notices.filter((notice) => {
      if (notice.paths?.length) {
        const matches = notice.paths.some((p) =>
          matchPath({ path: `/:locale${p}`, end: false, caseSensitive: false }, location.pathname)
        )
        if (!matches) {
          return false
        }
      }
      return shouldShowNotice(notice.id, notice.dismissType, notice.dismissDuration)
    })

    if (!isNoticeListEqual(filtered, prevFilteredRef.current)) {
      prevFilteredRef.current = filtered
      setVisibleNotices(filtered)
    }
  }, [notices, location.pathname])

  const contextValue = useMemo<PageNoticeContextValue>(
    () => ({ notices: visibleNotices, add, remove }),
    [visibleNotices, add, remove]
  )

  return <PageNoticeContext.Provider value={contextValue}>{children}</PageNoticeContext.Provider>
}

export interface UsePageNotice {
  addNotice: {
    neutral: (notice: AddPageNoticeAction) => string
    alert: (notice: AddPageNoticeAction) => string
    success: (notice: AddPageNoticeAction) => string
    attention: (notice: AddPageNoticeAction) => string
    info: (notice: AddPageNoticeAction) => string
  }
  removeNotice: PageNoticeContextValue['remove']
  notices: PageNoticeContextValue['notices']
  dismissNotice: (
    id: string,
    dismissType: NoticeProps['dismissType'],
    dismissDuration?: number
  ) => void
}

export const usePageNotice = (): UsePageNotice => {
  const context = useContext(PageNoticeContext)
  if (context === undefined) {
    throw new Error('usePageNotice must be used within a PageNoticeContextProvider')
  }

  const neutral = useCallback(
    (x: AddPageNoticeAction) => context.add({ ...x, type: 'neutral' }),
    [context]
  )
  const alert = useCallback(
    (x: AddPageNoticeAction) => context.add({ ...x, type: 'danger' }),
    [context]
  )
  const success = useCallback(
    (x: AddPageNoticeAction) => context.add({ ...x, type: 'success' }),
    [context]
  )
  const attention = useCallback(
    (x: AddPageNoticeAction) => context.add({ ...x, type: 'warning' }),
    [context]
  )
  const info = useCallback(
    (x: AddPageNoticeAction) => context.add({ ...x, type: 'info' }),
    [context]
  )

  const dark = useCallback(
    (x: AddPageNoticeAction) => context.add({ ...x, type: 'dark' }),
    [context]
  )

  const addNotice = useMemo(
    () => ({ neutral, alert, success, attention, info, dark }),
    [neutral, alert, success, attention, info, dark]
  )

  const dismissNotice = useCallback(
    (id: string, dismissType: NoticeProps['dismissType'], dismissDuration?: number) => {
      setNoticeState(id, true, dismissType, dismissDuration)
      context.remove(id)
    },
    [context]
  )

  return useMemo(
    () => ({ addNotice, notices: context.notices, removeNotice: context.remove, dismissNotice }),
    [addNotice, context.notices, context.remove, dismissNotice]
  )
}
